import React from 'react';
import { withTranslation } from 'react-i18next';
import { Row, Col } from 'react-bootstrap';
import { ProductService } from '../../services';
import { DefaultButton, LoadingButton } from '../buttons';
import { TextField } from '../fields';
import { ModalLayout } from '../layouts';

import '../../styles/modals/modal.css';

/**
 * Internal only: find a venue on Google by name alone, then read what is publicly
 * known about it. Nothing is written; this is the step before a menu import.
 */
class VenueLookupModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.initialState();
    this.productService = new ProductService();
  }

  initialState() {
    return { show: false, query: '', searching: false, loading_place: false, reading: false, error: null, places: [], place: null, stub: false, products: [], source_url: null };
  }

  componentDidMount() {
    if (this.props.onRef) this.props.onRef(this);
  }

  componentWillUnmount() {
    if (this.props.onRef) this.props.onRef(undefined);
  }

  show(show) {
    this.setState({ ...this.initialState(), show });
  }

  errorFrom(e, fallback) {
    return (e && e.errors && e.errors[0]) || (e && e.message) || fallback;
  }

  search = async () => {
    const query = this.state.query.trim();
    if (!query) {
      this.setState({ error: 'Type a venue name' });
      return;
    }

    this.setState({ searching: true, error: null, places: [], place: null });
    try {
      const result = await this.productService.searchVenues(query);
      if (result && result.success) {
        this.setState({ searching: false, places: result.places || [], stub: result.stub === true, error: (result.places || []).length ? null : 'No venues found' });
      } else {
        this.setState({ searching: false, error: this.errorFrom(result, 'Lookup failed') });
      }
    } catch (e) {
      this.setState({ searching: false, error: this.errorFrom(e, 'Lookup failed') });
    }
  };

  select = async (place_id) => {
    this.setState({ loading_place: true, error: null, place: null, products: [], source_url: null });
    try {
      const result = await this.productService.getVenueDetails(place_id);
      if (result && result.success) {
        this.setState({ loading_place: false, place: result.place, stub: result.stub === true });
      } else {
        this.setState({ loading_place: false, error: this.errorFrom(result, 'Could not read that venue') });
      }
    } catch (e) {
      this.setState({ loading_place: false, error: this.errorFrom(e, 'Could not read that venue') });
    }
  };

  readMenu = async () => {
    const { place } = this.state;
    if (!place || !place.website) return;

    this.setState({ reading: true, error: null, products: [], source_url: null });
    try {
      const result = await this.productService.fetchMenuFromWeb(place.website);
      if (result && result.success) {
        this.setState({ reading: false, products: result.products || [], source_url: result.source_url || null });
      } else {
        this.setState({ reading: false, error: this.errorFrom(result, 'Could not read a menu'), source_url: (result && result.source_url) || null });
      }
    } catch (e) {
      this.setState({ reading: false, error: this.errorFrom(e, 'Could not read a menu') });
    }
  };

  /** Only the flags that say something about how they trade today. */
  flagLabels(flags) {
    if (!flags) return [];
    const map = [
      ['delivery', 'Delivery'],
      ['takeout', 'Collection'],
      ['dine_in', 'Dine in'],
      ['curbside_pickup', 'Kerbside'],
      ['serves_beer', 'Beer'],
      ['serves_wine', 'Wine'],
      ['serves_breakfast', 'Breakfast'],
      ['serves_lunch', 'Lunch'],
      ['serves_dinner', 'Dinner'],
      ['outdoor_seating', 'Outdoor seating'],
    ];
    return map.filter(([key]) => flags[key] === true).map(([, label]) => label);
  }

  render() {
    const { t } = this.props;
    const { show, query, searching, loading_place, reading, error, places, place, stub, products, source_url } = this.state;

    return (
      show && (
        <ModalLayout title={t('Find venue')} subtitle={stub ? t('Sample data, Google billing is not active') : null} width={products.length ? 820 : place ? 720 : 620}>
          <Row>
            <Col md={9}>
              <TextField
                fullWidth
                label={t('Venue name')}
                value={query}
                onChange={(e) => this.setState({ query: e.target.value, error: null })}
                onKeyDown={(e) => e.key === 'Enter' && this.search()}
              />
            </Col>
            <Col md={3} style={{ paddingTop: 14 }}>
              <LoadingButton text={t('SEARCH')} state={searching ? 'LOADING' : 'DONE'} onClick={this.search} fullWidth />
            </Col>
          </Row>

          <p style={{ marginTop: 8, opacity: 0.7 }}>{t('A name and a town is enough, for example "the queen’s arms kensington".')}</p>

          {error && (
            <span className='modal-error-text' style={{ marginTop: 14 }}>
              {error}
            </span>
          )}

          {places.length > 0 && !place && (
            <>
              <p style={{ marginTop: 20 }}>
                <b>
                  {places.length} {t('matches')}
                </b>
              </p>
              {places.map((p) => (
                <div key={p.place_id} style={{ borderTop: '1px solid #eee', paddingTop: 12, marginTop: 8 }}>
                  <Row>
                    <Col md={9}>
                      <b>{p.name}</b>
                      <div style={{ opacity: 0.7 }}>{p.address}</div>
                      {p.type ? <div style={{ opacity: 0.5 }}>{p.type}</div> : null}
                    </Col>
                    <Col md={3} style={{ paddingTop: 6 }}>
                      <DefaultButton text={t('SELECT')} onClick={() => this.select(p.place_id)} fullWidth />
                    </Col>
                  </Row>
                </div>
              ))}
            </>
          )}

          {loading_place && <p style={{ marginTop: 14, textAlign: 'center' }}>{t('Reading the venue')}</p>}

          {place && (
            <>
              <div style={{ borderTop: '1px solid #eee', paddingTop: 16, marginTop: 20 }}>
                <h5>{place.name}</h5>
                <p style={{ marginBottom: 6 }}>{place.address}</p>
                {place.phone ? <p style={{ marginBottom: 6 }}>{place.phone}</p> : null}
                {place.website ? (
                  <p style={{ marginBottom: 6 }}>
                    <a href={place.website} target='_blank' rel='noreferrer'>
                      {place.website}
                    </a>
                  </p>
                ) : (
                  <p style={{ marginBottom: 6, opacity: 0.6 }}>{t('No website listed')}</p>
                )}
                <p style={{ marginBottom: 6 }}>
                  {(place.photos || []).length} {t('photos available')}
                </p>
                <p style={{ marginBottom: 0 }}>
                  {this.flagLabels(place.flags).length ? this.flagLabels(place.flags).join(' · ') : t('No service details listed')}
                </p>
              </div>

              <p style={{ marginTop: 16, opacity: 0.7 }}>
                {t('Google does not carry the menu itself, so it is read from the venue\u2019s own website.')}
              </p>

              {place.website && (
                <div className='modal-buttons-container' style={{ marginTop: 4 }}>
                  <div className='modal-button-full'>
                    <LoadingButton text={t('READ MENU FROM WEBSITE')} state={reading ? 'LOADING' : 'DONE'} onClick={this.readMenu} fullWidth />
                  </div>
                </div>
              )}

              {reading && <p style={{ marginTop: 14, textAlign: 'center' }}>{t('Finding and reading the menu. This can take a minute.')}</p>}

              {products.length > 0 && (
                <>
                  <p style={{ marginTop: 20 }}>
                    <b>
                      {products.length} {t('items read')}
                    </b>{' '}
                    {source_url ? (
                      <a href={source_url} target='_blank' rel='noreferrer'>
                        {t('source')}
                      </a>
                    ) : null}
                  </p>
                  {products.map((product, index) => (
                    <Row key={index} style={{ borderTop: '1px solid #eee', paddingTop: 8, marginTop: 6 }}>
                      <Col md={5}>{product.name}</Col>
                      <Col md={3} style={{ opacity: 0.7 }}>{product.category}</Col>
                      <Col md={4} style={{ textAlign: 'right' }}>
                        {(product.prices || []).map((price) => `${price.name} ${price.price}`).join('  \u00b7  ')}
                      </Col>
                    </Row>
                  ))}
                  <p style={{ marginTop: 16, opacity: 0.7 }}>
                    {t('Nothing has been saved. Use Import menu on the products page to create these against a venue.')}
                  </p>
                </>
              )}
            </>
          )}

          <div className='modal-buttons-container'>
            <div className={place ? 'modal-button' : 'modal-button-full'}>
              <DefaultButton text={t('CLOSE')} onClick={() => this.show(false)} fullWidth />
            </div>
            {place && (
              <div className='modal-button'>
                <DefaultButton text={t('BACK TO RESULTS')} onClick={() => this.setState({ place: null, error: null, products: [], source_url: null })} fullWidth />
              </div>
            )}
          </div>
        </ModalLayout>
      )
    );
  }
}

export default withTranslation()(VenueLookupModal);
